import { jsonForScript } from "./htmlViewer/bootstrapScript";

export const intercomIframeSandbox =
  "allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads";

export type IntercomBootMessage = {
  type: "3b-intercom-boot";
  email: string;
  name: string;
  userJwt?: string;
  company: { id: string; name: string };
  path: string;
  colorScheme: "light" | "dark";
};

export type IntercomShowMessage = { type: "3b-intercom-show" };

export type IntercomNavigateMessage = {
  type: "3b-intercom-navigate";
  path: string;
};

export type IntercomThemeMessage = {
  type: "3b-intercom-theme";
  colorScheme: "light" | "dark";
};

export type IntercomVisibilityMessage = {
  type: "3b-intercom-visibility";
  visible: boolean;
};

export type IntercomReadyMessage = {
  type: "3b-intercom-ready";
  appId?: string;
};

export type IntercomUnreadMessage = {
  type: "3b-intercom-unread";
  count: number;
};

export function intercomShellHtml(opts: {
  parentOrigin: string;
  appId: string;
  apiBase: string;
  nonce: string;
}): string {
  const script = `(function () {
    var PARENT_ORIGIN = ${jsonForScript(opts.parentOrigin)};
    var APP_ID = ${jsonForScript(opts.appId)};
    var API_BASE = ${jsonForScript(opts.apiBase)};
    var booted = false;

    var queue = function () { queue.c(arguments); };
    queue.q = [];
    queue.c = function (args) { queue.q.push(args); };
    window.Intercom = queue;

    function notify(visible) {
      parent.postMessage(
        { type: "3b-intercom-visibility", visible: visible },
        PARENT_ORIGIN
      );
    }

    // Intercom reads page views from this frame's own location, so mirror the
    // app's path onto it (the origin can't be changed, only the path).
    function setPath(path) {
      if (typeof path !== "string") return;
      if (path.charAt(0) !== "/" || path.charAt(1) === "/") return;
      history.replaceState(null, "", path);
    }

    // A cross-origin iframe whose used color scheme differs from its
    // embedder's gets an opaque canvas painted behind it, so the shell must
    // mirror the app's scheme to stay transparent; theme_mode keeps the
    // messenger itself visually in step too.
    function setColorScheme(scheme) {
      if (scheme !== "light" && scheme !== "dark") return null;
      document.documentElement.style.colorScheme = scheme;
      return scheme;
    }

    window.addEventListener("message", function (event) {
      if (event.origin !== PARENT_ORIGIN) return;
      if (event.source !== window.parent) return;
      var data = event.data;
      if (!data || typeof data !== "object") return;
      if (data.type === "3b-intercom-boot" && !booted) {
        booted = true;
        setPath(data.path);
        var settings = {
          app_id: APP_ID,
          api_base: API_BASE,
          hide_default_launcher: true,
        };
        var scheme = setColorScheme(data.colorScheme);
        if (scheme) settings.theme_mode = scheme;
        // Intercom reads identity from the token alone when there is one, so
        // sending these alongside it would leave a silently ignored copy.
        if (typeof data.userJwt === "string") {
          settings.intercom_user_jwt = data.userJwt;
        } else {
          settings.email = String(data.email);
          settings.name = String(data.name);
          if (data.company && typeof data.company === "object") {
            settings.company = {
              company_id: String(data.company.id),
              name: String(data.company.name),
            };
          }
        }
        window.intercomSettings = settings;
        window.Intercom("onShow", function () { notify(true); });
        window.Intercom("onHide", function () { notify(false); });
        window.Intercom("onUnreadCountChange", function (count) {
          parent.postMessage(
            { type: "3b-intercom-unread", count: count },
            PARENT_ORIGIN
          );
        });
        var widget = document.createElement("script");
        widget.async = true;
        widget.src = "https://widget.intercom.io/widget/" + APP_ID;
        document.head.appendChild(widget);
      } else if (data.type === "3b-intercom-show" && booted) {
        window.Intercom("show");
      } else if (data.type === "3b-intercom-navigate") {
        setPath(data.path);
        if (booted) window.Intercom("update");
      } else if (data.type === "3b-intercom-theme") {
        var updated = setColorScheme(data.colorScheme);
        if (updated && booted) {
          window.Intercom("update", { theme_mode: updated });
        }
      }
    });

    document.getElementById("background").addEventListener("click", function () {
      window.Intercom("hide");
    });

    // The parent reads prolonged silence after the frame's load event as a
    // shell that never ran, so this must be posted unconditionally.
    parent.postMessage(
      { type: "3b-intercom-ready", appId: APP_ID },
      PARENT_ORIGIN
    );
  })();`;

  return `<!DOCTYPE html>
          <html>
            <head>
              <meta charset="utf-8">
              <link rel="icon" href="data:,">
              <style>
                html, body { margin: 0; padding: 0; height: 100%; background: transparent; }
                #background { position: fixed; inset: 0; }
              </style>
            </head>
            <body>
              <div id="background"></div>
              <script nonce="${opts.nonce}">${script}</script>
            </body>
          </html>`;
}
